/**
 * `enrichWithAncestors` — resolve the producing-string ancestry of a set of
 * rows into per-relation `{ space, externalId, name }` struct cells.
 *
 * The enrichment path is exactly two BATCHED `view.query` calls, never an
 * N+1 fan-out:
 *
 *   1. Query `FlatHierarchy_Agent` for every distinct (non-null) string ref
 *      via a single `string in [...]` direct-relation filter. Each returned
 *      row carries the string's full ancestry as direct-relation columns
 *      (`company` / `asset` / `field` / `reservoir` / `location` / `well`),
 *      each a `{ space, externalId }` instance reference.
 *   2. Collect every distinct ancestor ref across all requested relations and
 *      query `Entity_Agent` for their display names via a single
 *      `externalId in [...]` filter, building a `externalId → name` map.
 *
 * The two reads are memoized through `ctx.session.fetch`, keyed by the sorted
 * ref set, so a diamond of views enriching the same strings within one
 * session collapses to a single hierarchy + entity round-trip.
 *
 * The result is, per relation, a `Cell[]` aligned 1:1 with the input
 * `stringRefs`: each cell is a `{ space, externalId, name }` `StructCell`
 * (with `name` looked up from the Entity read, or `null` when the entity is
 * unknown), or `null` where the row's string ref — or that ancestor on the
 * resolved hierarchy row — is missing.
 */

import type { CogniteClient, QueryRequest } from '@cognite/sdk';

import type { DataModelView } from '#core/lib/cognite/view.ts';
import { instancesToColumns, type MintSeriesSource, viewToSchema } from '#core/lib/cognite/view.ts';
import type { SourceFetcherCtx } from '#core/lib/expr/EvalContext.ts';
import { buildCdfViewQuery, type CdfViewNative } from '#core/lib/expr/providers/cdf-view.ts';
import { type ColumnType, nullableCol, stringCol, structCol } from '#core/lib/expr/schema/types.ts';
import type { Cell, StructCell } from '#core/lib/expr/value.ts';
import type { InstanceIdType } from '#core/schema/defs.ts';

import { colRef, op } from './ast.ts';
import { EntityAgentView, FlatHierarchyAgentView } from '#schema/generated/ipso/views.ts';

/** The hierarchy ancestor relations a per-string view may enrich. */
export type AncestorRelation = 'company' | 'asset' | 'field' | 'reservoir' | 'location' | 'well';

/**
 * The engine column type of every enriched ancestor column: a struct of the
 * CDF instance identity (`space`, `externalId`) plus the resolved display
 * `name` (nullable — the Entity read may not know the ancestor).
 */
export const ancestorStruct: ColumnType = structCol({
  space: stringCol,
  externalId: stringCol,
  name: nullableCol(stringCol),
});

/**
 * StringContext enrichment preset — populate well / field / reservoir ancestry
 * off the producing-string `wellString` ref. The single source for the
 * relation set every per-string view enriches with; catalog groups import this
 * rather than re-declaring the literal.
 */
export const STRING_CONTEXT_ENRICH: {
  readonly stringRefColumn: string;
  readonly relations: readonly AncestorRelation[];
} = {
  stringRefColumn: 'wellString',
  relations: ['well', 'field', 'reservoir'],
};
/**
 * Render the agent-doc ancestry tail for a standard per-string view from its
 * enrichment relation set — the single place `STRING_CONTEXT_ENRICH.relations`
 * is turned into prose, so the spec.description tails never drift from the SSOT.
 */
export const describeAncestry = (relations: readonly AncestorRelation[]): string =>
  `, enriched with ${relations.join('/')} ancestry.`;

/**
 * Render the agent-doc hierarchy-scope tail from the producing-string ref
 * column — the single place `STRING_CONTEXT_ENRICH.stringRefColumn` is turned
 * into the "filter by … to scope by hierarchy" guidance.
 */
export const describeStringScope = (stringRefColumn: string, entity = 'WellEntity'): string =>
  ` Filter by ${stringRefColumn} (or join ${entity}) to scope by hierarchy.`;

/**
 * The FlatHierarchy direct-relation column carrying the producing string —
 * the `in` filter target for the hierarchy read.
 */
export const STRING_REL_COLUMN = 'string';

/** Stable key for a CDF instance reference (space-qualified). */
const refKey = (ref: InstanceIdType): string => `${ref.space}\0${ref.externalId}`;

/**
 * Read a `{ space, externalId }` struct cell off a projected column, or
 * `null` when the cell is absent / not a struct ref.
 *
 * This is the canonical struct-ref decoding seam owned by the enrichment
 * module — the reshape / flatten factories import it rather than redefining.
 */
export const asRef = (cell: Cell | undefined): InstanceIdType | null => {
  if (cell === null || cell === undefined || typeof cell !== 'object') {
    return null;
  }
  const record = cell as StructCell;
  const space = record.space;
  const externalId = record.externalId;
  if (typeof space === 'string' && typeof externalId === 'string') {
    return { space, externalId };
  }
  return null;
};

/** Build the single-view `in` instance-query for a set of refs, or skip. */
const queryRefs = async (
  client: CogniteClient,
  view: DataModelView,
  filterColumn: string,
  values: readonly unknown[],
  columns: readonly string[],
  mintSeries: MintSeriesSource,
) => {
  if (values.length === 0) {
    return { columns: instancesToColumns([], viewToSchema(view), view, mintSeries), schema: viewToSchema(view) };
  }
  const native: CdfViewNative = {
    view: view.externalId,
    filter: op('in', colRef(filterColumn), values as readonly never[]),
    columns,
  };
  const payload: QueryRequest = buildCdfViewQuery(native, view);
  const rows = await view.query(client, payload);
  const schema = viewToSchema(view);
  return { columns: instancesToColumns(rows, schema, view, mintSeries), schema };
};

/**
 * Resolve each input ref's ancestors through `FlatHierarchy_Agent`, aligned 1:1
 * to `selfRefs`. `selfColumn` is the FlatHierarchy column the input refs match —
 * the producing-string column for a string-grain view (the default), or a
 * coarser ancestor relation column (e.g. `well`) for an entity view at that
 * grain, where each input ref fans out to several string rows that share its
 * ancestors (the last row per ref wins — they agree on the resolved ancestor).
 */
export const enrichWithAncestors = async (
  client: CogniteClient,
  ctx: SourceFetcherCtx,
  selfRefs: readonly (InstanceIdType | null)[],
  relations: readonly AncestorRelation[],
  mintSeries: MintSeriesSource,
  selfColumn: string = STRING_REL_COLUMN,
): Promise<Record<AncestorRelation, readonly Cell[]>> => {
  // Distinct, ordered self refs to resolve the hierarchy for.
  const presentSelf: readonly InstanceIdType[] = selfRefs.filter(
    (r: InstanceIdType | null): r is InstanceIdType => r !== null,
  );
  const distinctSelf: readonly InstanceIdType[] = Array.from(
    new Map(presentSelf.map((r: InstanceIdType) => [refKey(r), r] as const)).values(),
  ).sort((a: InstanceIdType, b: InstanceIdType) => refKey(a).localeCompare(refKey(b)));

  // (1) Hierarchy read — memoized per session by the self column, the sorted
  // relation set, and the sorted self-ref set. The relation set MUST be in the
  // key: it determines which ancestor keys each cached row carries, so a prior
  // query over the same selfs but a different relation set would otherwise hit a
  // stale row missing the requested relation. Yields: self-ref key → { relation:
  // ancestorRef|null } for that row.
  type HierarchyRow = Record<AncestorRelation, InstanceIdType | null>;
  const hierarchy = await ctx.session.fetch<Map<string, HierarchyRow>>(
    [
      'virtual-view:enrich:flat-hierarchy',
      FlatHierarchyAgentView.externalId,
      selfColumn,
      [...relations].sort(),
      distinctSelf.map(refKey),
    ],
    async (): Promise<Map<string, HierarchyRow>> => {
      const { columns } = await queryRefs(
        client,
        FlatHierarchyAgentView,
        selfColumn,
        distinctSelf,
        [selfColumn, ...relations],
        mintSeries,
      );
      const selfCells: readonly Cell[] = columns[selfColumn] ?? [];
      const entries = selfCells.flatMap((cell: Cell, ri: number): (readonly [string, HierarchyRow])[] => {
        const selfRef = asRef(cell);
        if (selfRef === null) {
          return [];
        }
        const ancestors = Object.fromEntries(
          relations.map((rel: AncestorRelation) => [rel, asRef(columns[rel]?.[ri])] as const),
        ) as HierarchyRow;
        return [[refKey(selfRef), ancestors] as const];
      });
      return new Map(entries);
    },
  );

  // Collect every distinct ancestor ref across relations for the name lookup.
  const allAncestorRefs: readonly InstanceIdType[] = Array.from(hierarchy.values()).flatMap(
    (ancestors: HierarchyRow): InstanceIdType[] =>
      relations
        .map((rel: AncestorRelation) => ancestors[rel])
        .filter((r: InstanceIdType | null): r is InstanceIdType => r !== null),
  );
  const ancestorRefs: readonly InstanceIdType[] = Array.from(
    new Map(allAncestorRefs.map((r: InstanceIdType) => [refKey(r), r] as const)).values(),
  ).sort((a: InstanceIdType, b: InstanceIdType) => refKey(a).localeCompare(refKey(b)));

  // (2) Entity read — memoized per session by the sorted ancestor-ref set.
  // Matched by externalId alone (single-space contract on the entity view).
  const namesByExternalId = await ctx.session.fetch<Map<string, string | null>>(
    ['virtual-view:enrich:entity', EntityAgentView.externalId, ancestorRefs.map((r: InstanceIdType) => r.externalId)],
    async (): Promise<Map<string, string | null>> => {
      const { columns } = await queryRefs(
        client,
        EntityAgentView,
        'externalId',
        ancestorRefs.map((r: InstanceIdType) => r.externalId),
        ['externalId', 'name'],
        mintSeries,
      );
      const externalIds: readonly Cell[] = columns.externalId ?? [];
      const names: readonly Cell[] = columns.name ?? [];
      return new Map(
        externalIds.map((id: Cell, i: number) => [id as string, (names[i] ?? null) as string | null] as const),
      );
    },
  );

  // Assemble, per relation, a Cell[] aligned 1:1 to the input selfRefs.
  const buildColumn = (rel: AncestorRelation): readonly Cell[] =>
    selfRefs.map((selfRef: InstanceIdType | null): Cell => {
      if (selfRef === null) {
        return null;
      }
      const ancestor = hierarchy.get(refKey(selfRef))?.[rel] ?? null;
      if (ancestor === null) {
        return null;
      }
      return {
        space: ancestor.space,
        externalId: ancestor.externalId,
        name: namesByExternalId.get(ancestor.externalId) ?? null,
      } satisfies StructCell;
    });

  return Object.fromEntries(relations.map((rel: AncestorRelation) => [rel, buildColumn(rel)] as const)) as Record<
    AncestorRelation,
    readonly Cell[]
  >;
};

/**
 * Distinct-descendant counts off FlatHierarchy, aligned 1:1 to `parentRefs`.
 *
 * The flat-row counterpart to {@link enrichWithAncestors}: where enrichment
 * resolves a single ancestor per row, this counts how many *distinct*
 * descendants each parent fans out to. Both share the same FlatHierarchy read
 * seam (`queryRefs` → `view.query`), the same space-qualified `refKey`
 * dedupe, and the same `ctx.session.fetch` memoization.
 *
 * A single FlatHierarchy `in` read is issued — filtered by `parentColumn` over
 * the distinct present `parentRefs`, projecting `parentColumn` plus every
 * `countColumn`. The returned rows are grouped by parent `refKey`; within each
 * group, each `countColumn`'s descendant refs are deduped by `refKey` and the
 * resulting set size is the count. Returns, per `countColumn`, a `Cell[]` of
 * integer counts aligned 1:1 to the input `parentRefs` — `0` where a parent
 * has no hierarchy rows (or `null` parent input).
 */
export const countDescendants = async (
  client: CogniteClient,
  ctx: SourceFetcherCtx,
  parentRefs: readonly (InstanceIdType | null)[],
  parentColumn: string,
  countColumns: readonly string[],
  mintSeries: MintSeriesSource,
): Promise<Record<string, readonly Cell[]>> => {
  // Distinct, ordered parent refs to count descendants for.
  const presentParents: readonly InstanceIdType[] = parentRefs.filter(
    (r: InstanceIdType | null): r is InstanceIdType => r !== null,
  );
  const distinctParents: readonly InstanceIdType[] = Array.from(
    new Map(presentParents.map((r: InstanceIdType) => [refKey(r), r] as const)).values(),
  ).sort((a: InstanceIdType, b: InstanceIdType) => refKey(a).localeCompare(refKey(b)));

  // Single FlatHierarchy read — memoized per session by the sorted parent-ref
  // set, the parent column, and the projected count columns.
  // Yields: parent-ref key → { countColumn → Set<distinct descendant refKey> }.
  type CountRow = Map<string, Set<string>>;
  const counts = await ctx.session.fetch<Map<string, CountRow>>(
    [
      'virtual-view:count:flat-hierarchy',
      FlatHierarchyAgentView.externalId,
      parentColumn,
      [...countColumns].sort(),
      distinctParents.map(refKey),
    ],
    async (): Promise<Map<string, CountRow>> => {
      const { columns } = await queryRefs(
        client,
        FlatHierarchyAgentView,
        parentColumn,
        distinctParents,
        [parentColumn, ...countColumns],
        mintSeries,
      );
      const parentCells: readonly Cell[] = columns[parentColumn] ?? [];
      const byParent = new Map<string, CountRow>();
      parentCells.forEach((cell: Cell, ri: number): void => {
        const parentRef = asRef(cell);
        if (parentRef === null) {
          return;
        }
        const key = refKey(parentRef);
        const row: CountRow =
          byParent.get(key) ?? new Map<string, Set<string>>(countColumns.map((c: string) => [c, new Set<string>()]));
        countColumns.forEach((c: string): void => {
          const descendant = asRef(columns[c]?.[ri]);
          if (descendant !== null) {
            row.get(c)?.add(refKey(descendant));
          }
        });
        byParent.set(key, row);
      });
      return byParent;
    },
  );

  // Assemble, per count column, a Cell[] aligned 1:1 to the input parentRefs.
  const buildColumn = (column: string): readonly Cell[] =>
    parentRefs.map((parentRef: InstanceIdType | null): Cell => {
      if (parentRef === null) {
        return null;
      }
      return counts.get(refKey(parentRef))?.get(column)?.size ?? 0;
    });

  return Object.fromEntries(countColumns.map((c: string) => [c, buildColumn(c)] as const));
};
